import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Calendar, Clock, MapPin } from "lucide-react";
import { useEvents, useFeaturedEvents } from "../hooks/useEvents";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const Events: React.FC = () => {
  const { data: events = [] } = useEvents();
  const { data: featuredEvents = [] } = useFeaturedEvents();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-lounge-dark text-lounge-light">
      <Navbar />

      {/* Page Header */}
      <div className="relative pt-20 pb-16 md:pt-24 md:pb-20 bg-lounge-charcoal">
        <div className="container-custom">
          <h1 className="section-title text-center">Upcoming Events</h1>
          <p className="section-subtitle text-center max-w-3xl mx-auto">
            Join us for special nights, themed parties, live performances, and more at Seven8Nine Hookah Lounge.
          </p>
        </div>
      </div>

      {/* Featured Events */}
      <section className="section-padding bg-lounge-dark">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-8 text-lounge-gold">Featured Events</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {featuredEvents.map(event => (
              <div 
                key={event.id}
                id={`event-${event.id}`}
                className="bg-lounge-charcoal rounded-lg overflow-hidden border border-lounge-gold/30 shadow-lg"
              >
                <div className="relative h-64">
                  <img 
                    src={event.image_url} 
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-lounge-gold text-lounge-dark px-4 py-2 rounded-md font-display font-semibold">
                    Featured
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-display font-semibold mb-3 text-lounge-gold">{event.title}</h3>
                  
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex items-center">
                      <Calendar size={16} className="text-lounge-gold mr-2" />
                      <span className="text-lounge-light/70">
                        {format(new Date(event.date), "MMMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Clock size={16} className="text-lounge-gold mr-2" />
                      <span className="text-lounge-light/70">
                        {format(new Date(`2000-01-01T${event.start_time}`), "h:mm a")} - {format(new Date(`2000-01-01T${event.end_time}`), "h:mm a")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <MapPin size={16} className="text-lounge-gold mr-2" />
                      <span className="text-lounge-light/70">Seven8Nine Lounge</span>
                    </div>
                  </div>
                  
                  <p className="text-lounge-light/80 mb-6">{event.description}</p>
                  
                  <div className="flex flex-wrap gap-4">
                    <button
                      className="btn-outline"
                      onClick={() => navigate('/contact?subject=book-event')}
                    >
                      More Info
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Events */}
      <section className="section-padding bg-gradient-to-b from-lounge-dark to-lounge-charcoal">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-8 text-lounge-gold">All Upcoming Events</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map(event => (
              <div 
                key={event.id}
                id={`event-${event.id}`}
                className="bg-lounge-charcoal rounded-lg overflow-hidden border border-lounge-gold/20 hover:border-lounge-gold/40 transition-all duration-300"
              >
                <div className="h-48">
                  <img 
                    src={event.image_url} 
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-display font-semibold mb-2 text-lounge-gold">{event.title}</h3>
                  
                  <div className="flex flex-wrap gap-3 mb-3">
                    <div className="flex items-center">
                      <Calendar size={14} className="text-lounge-gold mr-1" />
                      <span className="text-sm text-lounge-light/70">
                        {format(new Date(event.date), "MMMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Clock size={14} className="text-lounge-gold mr-1" />
                      <span className="text-sm text-lounge-light/70">
                        {format(new Date(`2000-01-01T${event.start_time}`), "h:mm a")} - {format(new Date(`2000-01-01T${event.end_time}`), "h:mm a")}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-lounge-light/80 text-sm mb-4 line-clamp-2">{event.description}</p>
                  
                  <button className="text-lounge-gold hover:text-lounge-light font-semibold transition-colors text-sm">
                    Learn More &rarr;
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Host Your Event */}
      <section className="section-padding bg-lounge-charcoal">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-display font-bold mb-6 text-lounge-gold">Host Your Event at Seven8Nine</h2>
            <p className="text-lounge-light/80 mb-8">
              Looking for a unique venue for your next celebration or gathering? Seven8Nine Hookah Lounge offers event hosting services with customizable packages to suit your needs. From birthdays and bachelor/bachelorette parties to corporate events, we can accommodate groups of various sizes.
            </p>
            <button
              className="btn-primary inline-block"
              onClick={() => navigate('/contact?subject=general')}
            >
              Inquire About Event Hosting
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Events;
